// Mock data for development and testing

// Generate mock calendar data for the current month
const generateMockCalendarData = () => {
  const calendar = [];
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isPast = date < today;
    const isToday = date.toDateString() === today.toDateString();

    let status: 'present' | 'absent' | 'leave' | 'holiday' | 'weekend' | 'partial' = 'absent';
    let punchInTime = null;
    let punchOutTime = null;
    let totalHours = 0;
    let notes = '';

    if (isPast || isToday) {
      if (isWeekend) {
        status = 'weekend';
      } else if (Math.random() > 0.15) { // 85% attendance rate
        const attendanceType = Math.random();

        if (attendanceType > 0.1) { // 90% complete attendance
          status = 'present';
          // Generate realistic punch times with some variation
          const baseInHour = 8 + Math.floor(Math.random() * 2); // 8-9 AM
          const baseInMinute = Math.floor(Math.random() * 60);
          const baseOutHour = 17 + Math.floor(Math.random() * 2); // 5-6 PM
          const baseOutMinute = Math.floor(Math.random() * 60);

          punchInTime = new Date(date);
          punchInTime.setHours(baseInHour, baseInMinute, 0, 0);

          punchOutTime = new Date(date);
          punchOutTime.setHours(baseOutHour, baseOutMinute, 0, 0);

          totalHours = (punchOutTime.getTime() - punchInTime.getTime()) / (1000 * 60 * 60);
          notes = 'Regular working day';
        } else { // 10% partial attendance (only punch-in or only punch-out)
          status = 'partial';
          const baseInHour = 8 + Math.floor(Math.random() * 2); // 8-9 AM
          const baseInMinute = Math.floor(Math.random() * 60);

          punchInTime = new Date(date);
          punchInTime.setHours(baseInHour, baseInMinute, 0, 0);

          // Randomly decide if it's missing punch-out (more common) or punch-in
          if (Math.random() > 0.2) {
            // Missing punch-out (80% of partial cases)
            punchOutTime = null;
            notes = 'Missing punch-out';
          } else {
            // Missing punch-in (20% of partial cases)
            punchInTime = null;
            const baseOutHour = 17 + Math.floor(Math.random() * 2);
            const baseOutMinute = Math.floor(Math.random() * 60);
            punchOutTime = new Date(date);
            punchOutTime.setHours(baseOutHour, baseOutMinute, 0, 0);
            notes = 'Missing punch-in';
          }

          totalHours = 0; // Can't calculate without both times
        }
      } else {
        status = Math.random() > 0.5 ? 'absent' : 'leave';
        notes = status === 'absent' ? 'Sick leave' : 'Annual leave';
      }
    } else {
      // Future dates
      if (isWeekend) {
        status = 'weekend';
      } else {
        status = 'present'; // Future working days (using present as fallback)
      }
    }

    calendar.push({
      date: date.toISOString().split('T')[0],
      status,
      attendance: (status === 'present' || status === 'partial') ? {
        clockIns: punchInTime ? [punchInTime.toISOString()] : [],
        clockOuts: punchOutTime ? [punchOutTime.toISOString()] : [],
        totalMinutes: Math.round(totalHours * 60),
        isComplete: !!(punchInTime && punchOutTime),
      } : undefined,
      leave: status === 'leave' ? {
        leaveType: 'Annual Leave',
        leaveTypeColor: '#10B981',
        reason: notes,
        status: 'approved',
      } : undefined,
      holiday: status === 'holiday' ? {
        name: 'Holiday',
        type: 'public',
        color: '#F59E0B',
      } : undefined,
      isWorkingDay: !isWeekend && status !== 'holiday',
      displayText: status === 'present' ? `${totalHours}h` : status.charAt(0).toUpperCase() + status.slice(1),
    });
  }

  return calendar;
};

export const mockCalendarData = {
  calendar: generateMockCalendarData(),
  summary: {
    totalDays: 30,
    workingDays: 22,
    presentDays: 18,
    absentDays: 2,
    leaveDays: 1,
    holidayDays: 1,
  },
};

export const mockAttendanceStats = {
  monthly: {
    totalWorkingDays: 22,
    presentDays: 18,
    absentDays: 2,
    leaveDays: 1,
    holidayDays: 1,
    partialDays: 0,
    totalWorkingHours: 144,
    averageWorkingHours: 8,
  },
  weekly: {
    totalWorkingDays: 5,
    presentDays: 4,
    absentDays: 1,
    leaveDays: 0,
    holidayDays: 0,
    partialDays: 0,
    totalWorkingHours: 32,
    averageWorkingHours: 8,
  },
  period: {
    monthStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
    weekStart: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    current: new Date().toISOString(),
  },
};

// Mock attendance status with state management and localStorage persistence
class MockAttendanceState {
  private storageKey = 'erpca-mock-attendance-state';
  private isActive: boolean = false;
  private clockInTime: Date | null = null;
  private todayTotal: number = 0;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.isActive = data.isActive || false;
        this.clockInTime = data.clockInTime ? new Date(data.clockInTime) : null;
        this.todayTotal = data.todayTotal || 0;

        // Reset if it's a new day
        const today = new Date().toDateString();
        const storedDate = data.date;
        if (storedDate !== today) {
          this.todayTotal = 0;
          this.isActive = false;
          this.clockInTime = null;
          this.saveToStorage();
        }
      }
    } catch (error) {
      console.warn('Failed to load mock attendance state from storage:', error);
    }
  }

  private saveToStorage() {
    try {
      const data = {
        isActive: this.isActive,
        clockInTime: this.clockInTime?.toISOString(),
        todayTotal: this.todayTotal,
        date: new Date().toDateString(),
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save mock attendance state to storage:', error);
    }
  }

  getStatus() {
    return {
      isActive: this.isActive,
      currentSession: this.isActive && this.clockInTime ? {
        clockInTime: this.clockInTime.toISOString(),
        duration: Math.floor((Date.now() - this.clockInTime.getTime()) / (1000 * 60)), // minutes
      } : undefined,
      todayTotal: this.todayTotal,
      canClockIn: !this.isActive,
      canClockOut: this.isActive,
    };
  }

  clockIn() {
    console.log('🟢 Mock: Clocking in...');
    this.isActive = true;
    this.clockInTime = new Date();
    this.saveToStorage();
    console.log('✅ Mock: Clocked in at', this.clockInTime.toISOString());
  }

  clockOut() {
    console.log('🔴 Mock: Clocking out...');
    if (this.isActive && this.clockInTime) {
      const sessionMinutes = Math.floor((Date.now() - this.clockInTime.getTime()) / (1000 * 60));
      this.todayTotal += sessionMinutes;
      console.log('📊 Mock: Session duration:', sessionMinutes, 'minutes. Total today:', this.todayTotal);
    }
    this.isActive = false;
    this.clockInTime = null;
    this.saveToStorage();
    console.log('✅ Mock: Clocked out successfully');
  }

  reset() {
    console.log('🔄 Mock: Resetting attendance state');
    this.isActive = false;
    this.clockInTime = null;
    this.todayTotal = 0;
    this.saveToStorage();
  }
}

// Global mock state instance
const mockState = new MockAttendanceState();

// Export functions to control mock state
export const mockAttendanceActions = {
  getStatus: () => mockState.getStatus(),
  clockIn: () => mockState.clockIn(),
  clockOut: () => mockState.clockOut(),
  reset: () => mockState.reset(),
};

// For backward compatibility, but this should be replaced with mockAttendanceActions.getStatus()
export const mockAttendanceStatus = mockState.getStatus();
